import { useState } from 'react';
import { Check, X } from 'lucide-react';
import { useToast } from '@/components/Toast';
import { useEngagementSearch, useSubmitDiscontinuation, type EngagementSearchRow } from '@/api/hooks';
import { submitErrorMessage } from '@/lib/formErrors';

import { todayLocalISO as todayISO } from '@/lib/date';
const label = (e: EngagementSearchRow) =>
  `${e.EngagementID} · ${e.ClientName} · ${e.ServiceCategory} / ${e.TemplateName}${e.Year ? ` (${e.Year})` : ''}`;

/** Item 17: pick the engagement(s) directly from current engagements, searchable like
 *  View / Change Engagements. One request can span clients. */
export function DiscontinuationRequest() {
  const notify = useToast();
  const submit = useSubmitDiscontinuation();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const results = useEngagementSearch(search);
  const [selected, setSelected] = useState<Record<string, EngagementSearchRow>>({});
  const [effectiveDate, setEffectiveDate] = useState(todayISO());
  const [notes, setNotes] = useState('');

  const picked = Object.values(selected);
  const rows = results.data ?? [];
  const searched = search.trim().length >= 2;
  const invalid = picked.length === 0 || !effectiveDate;
  const busy = submit.isPending;

  function toggle(e: EngagementSearchRow) {
    setSelected((s) => {
      const n = { ...s };
      if (n[e.EngagementID]) delete n[e.EngagementID];
      else n[e.EngagementID] = e;
      return n;
    });
  }

  async function onSubmit() {
    try {
      const res = await submit.mutateAsync({
        engagements: picked.map((e) => ({
          engagementId: e.EngagementID, serviceCategory: e.ServiceCategory, templateName: e.TemplateName,
          clientId: e.ClientID, clientName: e.ClientName,
        })),
        effectiveDate,
        notes,
      });
      notify('success', `Discontinuation request #${res.submissionId} submitted. A receipt has been emailed to you.`);
      setSelected({}); setEffectiveDate(todayISO()); setNotes('');
    } catch (e) {
      notify('error', submitErrorMessage(e));
    }
  }

  return (
    <div className="w-full max-w-4xl space-y-4 p-6">
      <div className="card">
        <h2 className="text-lg font-semibold text-foreground">Engagement Discontinuation Request</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Search for the engagement(s) to discontinue and set the effective date. The clients remain active. On submit,
          you receive a receipt and the admin team is notified.
        </p>
      </div>

      <div className="card space-y-4">
        <div className="space-y-2">
          <label className="label">Engagement(s) to discontinue *</label>
          {picked.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {picked.map((e) => (
                <span key={e.EngagementID} className="inline-flex items-center gap-1 rounded bg-secondary px-2 py-0.5 text-xs">
                  {label(e)}
                  {!busy && (
                    <button type="button" aria-label="Remove" className="text-muted-foreground hover:text-primary" onClick={() => toggle(e)}>
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
          <div>
            <label className="label">Search (Client, Engagement ID, Template or Service)</label>
            <div className="flex gap-2">
              <input className="input" placeholder="e.g. Smith, 10159618, Individual Tax, Assurance… then Enter"
                value={searchInput} disabled={busy}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && setSearch(searchInput)} />
              <button className="btn-secondary" type="button" disabled={busy} onClick={() => setSearch(searchInput)}>Search</button>
            </div>
          </div>
          {searched && (
            <div className="max-h-72 overflow-auto rounded-md border border-border">
              {results.isFetching && <div className="px-3 py-2 text-sm text-muted-foreground">Searching…</div>}
              {!results.isFetching && rows.length === 0 && (
                <div className="px-3 py-2 text-sm text-muted-foreground">No current engagements match.</div>
              )}
              {rows.map((e) => {
                const on = !!selected[e.EngagementID];
                return (
                  <button key={e.EngagementID} type="button" disabled={busy}
                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-secondary/60 ${on ? 'bg-secondary/40' : ''}`}
                    onClick={() => toggle(e)}>
                    <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${on ? 'border-primary bg-primary text-primary-foreground' : 'border-border'}`}>
                      {on && <Check className="h-3 w-3" />}
                    </span>
                    <span>
                      <b>{e.EngagementID}</b> · {e.ClientID} - {e.ClientName} · {e.ServiceCategory} / {e.TemplateName} {e.Year ? `(${e.Year})` : ''}
                      {e.Requested ? <span className="ml-1 text-accent">(discontinuation requested)</span> : null}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {searched && rows.length === 50 && (
            <p className="text-xs text-muted-foreground">Showing the first 50 matches. Narrow the search to see more.</p>
          )}
        </div>

        <div className="max-w-xs">
          <label className="label">Effective Discontinuation Date *</label>
          <input type="date" className="input" value={effectiveDate} disabled={busy} onChange={(e) => setEffectiveDate(e.target.value)} />
        </div>

        <div>
          <label className="label">Notes</label>
          <textarea className="input" rows={2} value={notes} disabled={busy} onChange={(e) => setNotes(e.target.value)} />
        </div>

        <p className="text-xs font-semibold text-accent">*  Mandatory Fields</p>

        <div>
          <button className="btn-success" disabled={invalid || busy} onClick={onSubmit}>
            {busy ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}
